import React, { Fragment, FC } from 'react';
import { compose } from 'recompose';
import { useTranslation } from 'react-i18next';
import { Formik, Form as FormikForm } from 'formik';
import { omit } from 'lodash';
import * as Yup from 'yup';

import { AuthenticationType, UserProfileInput } from 'api';
import { ActionContainer, SubmitButton, ValidatedSelect, ValidatedTextField } from 'components';
import { useClient, useCurrentUser } from 'hooks';
import { Terms } from 'features/auth';
import {
  withClientDepartments,
  withClientLocations,
  IWithClientDepartments,
  IWithClientLocations,
} from 'providers/queries';

interface IProps {
  working: boolean;
  onSubmit: (values: UserProfileInput) => void;
}

interface IForm extends UserProfileInput {
  acceptTerms: boolean;
}

interface IPropsEnhanced extends IProps, IWithClientDepartments, IWithClientLocations {}

const OnboardingForm: FC<IPropsEnhanced> = props => {
  const { clientDepartments, clientLocations, onSubmit, working } = props;
  const { t } = useTranslation();
  const currentUser = useCurrentUser();
  const client = useClient();

  const {
    authentication_type,
    locations_in_signup_required,
    departments_in_signup_required,
  } = client;
  const { full_name, job_title, department_id, location_id } = currentUser;

  const required: string = t('common:required');
  const isSSO = authentication_type === AuthenticationType.Sso;

  const locationValidation = locations_in_signup_required
    ? Yup.string().required(required)
    : Yup.string().nullable(true);

  const departmentValidation = departments_in_signup_required
    ? Yup.string().required(required)
    : Yup.string().nullable(true);

  const validationSchema = Yup.object().shape({
    id: Yup.string().required(),
    full_name: Yup.string().required(required),
    job_title: Yup.string().required(required),
    location_id: locationValidation,
    department_id: departmentValidation,
    acceptTerms: Yup.boolean().oneOf([true], t('auth:acceptTermsError')),
  });

  const initialValues: IForm = {
    id: currentUser.id,
    full_name: full_name || '',
    job_title: job_title || '',
    department_id: department_id || '',
    location_id: location_id || '',
    acceptTerms: !isSSO,
  };

  function handleSubmit(values: IForm) {
    onSubmit(omit(values, ['acceptTerms']));
  }

  function renderSelects() {
    const hasLocations = !!clientLocations.client_locations.length;
    const hasDepartments = !!clientDepartments.client_departments.length;

    return (
      <Fragment>
        {hasLocations && (
          <ValidatedSelect
            name="location_id"
            i18nKey="user:location"
            required={locations_in_signup_required}
            items={clientLocations.client_locations}
          />
        )}

        {hasDepartments && (
          <ValidatedSelect
            name="department_id"
            i18nKey="user:department"
            required={departments_in_signup_required}
            items={clientDepartments.client_departments}
          />
        )}
      </Fragment>
    );
  }

  return (
    <Formik
      onSubmit={handleSubmit}
      initialValues={initialValues}
      validationSchema={validationSchema}>
      <FormikForm>
        <ValidatedTextField name="full_name" i18nKey="user:fullName" />
        <ValidatedTextField name="job_title" i18nKey="user:title" />
        {renderSelects()}
        {isSSO && <Terms />}
        <ActionContainer>
          <SubmitButton working={working} text={t('common:next')} />
        </ActionContainer>
      </FormikForm>
    </Formik>
  );
};

const enhancers = compose<IPropsEnhanced, IProps>(withClientDepartments, withClientLocations);

export default enhancers(OnboardingForm);
